import type { ReactNode } from 'react'

/**
 * Page chrome — two patterns, both with a **full-bleed** slate-100 title band (no side gutters):
 *
 * - **Canvas** (default): `.page-canvas` bleeds `-mx-8`; title band edge-to-edge; body uses `px-8`.
 *   Use with `lead` + inset `DataTableCard` (Matchups, Ratings, Admin, Datasets).
 * - **Stack** (`fillViewport`): `.page-content-stack` white card + embedded table (Standings).
 */
export function PageScrollLayout({
  title,
  subtitle,
  headerExtra,
  actions,
  chromeExtra,
  lead,
  fillViewport = false,
  growTable = false,
  children,
}: {
  title: string
  subtitle?: ReactNode
  headerExtra?: ReactNode
  actions?: ReactNode
  /** @deprecated Prefer `lead` — kept for pages that still pass summary into the header band. */
  chromeExtra?: ReactNode
  /** Rendered between the title band and the table with margin (KPI cards, etc.). */
  lead?: ReactNode
  fillViewport?: boolean
  /** When true (with `lead` layout), the table block may grow to the bottom of the outlet. */
  growTable?: boolean
  children: ReactNode
}) {
  const headerInner = (
    <>
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="min-w-0">
          <h1 className="text-2xl font-bold text-slate-900">{title}</h1>
          {subtitle && <p className="mt-1 text-sm text-slate-600">{subtitle}</p>}
        </div>
        {headerExtra && <div className="flex flex-wrap items-center gap-3">{headerExtra}</div>}
      </div>
      {actions && <div className="mt-3 flex flex-wrap items-center gap-3">{actions}</div>}
      {chromeExtra && <div className="mt-4">{chromeExtra}</div>}
    </>
  )

  const headerBand = (
    <div className="page-shell-header shrink-0 px-8 pt-5 pb-4">
      {headerInner}
    </div>
  )

  const leadBlock = lead ? <div className="mb-4 shrink-0">{lead}</div> : null

  if (fillViewport) {
    return (
      <div className="page-content-stack -mx-8 flex min-h-full flex-1 flex-col overflow-hidden">
        {headerBand}
        <div className="page-shell-body flex min-h-0 flex-1 flex-col overflow-hidden">{children}</div>
      </div>
    )
  }

  return (
    <div
      className={[
        'page-canvas -mx-8 flex min-h-0 flex-col',
        growTable ? 'min-h-full flex-1' : '',
      ].join(' ')}
    >
      {headerBand}
      <div
        className={[
          'flex min-h-0 flex-col px-8',
          growTable ? 'min-h-0 flex-1 pb-8' : 'gap-4 pb-8',
        ].join(' ')}
      >
        {leadBlock}
        <div
          className={[
            'flex min-h-0 flex-col',
            growTable ? 'min-h-0 flex-1' : '',
          ].join(' ')}
        >
          {children}
        </div>
      </div>
    </div>
  )
}

/**
 * Table region with standard outlet inset (same as Ratings) — full `DataTableCard` border/radius.
 * Do not use `.page-content-stack` / `-mx-8` here; that pattern is only for `fillViewport` pages
 * where the title band and table share one stack (Standings).
 */
export function PageTableStack({
  grow,
  children,
}: {
  /** Grow to fill remaining outlet height (long tables). Otherwise height fits row count. */
  grow: boolean
  children: ReactNode
}) {
  return (
    <div
      className={[
        'table-viewport-region flex w-full flex-col',
        grow ? 'min-h-0 flex-1' : '',
      ].join(' ')}
    >
      {children}
    </div>
  )
}
